import React from "react";
import { PageHeader } from "../components/PageHeader/PageHeader";
import { SelectField, DateField } from "@makula/ui";

// ─── Types ────────────────────────────────────────────────────────────────────

type InvoiceStatus = "PAID" | "OVERDUE" | "SENT" | "DRAFT";
type TabFilter = "All" | "Sent" | "Overdue" | "Paid";

interface Invoice {
  id: string;
  number: string;
  company: string;
  quote: string | null;
  amount: string;
  status: InvoiceStatus;
  dueDate: string;
}

// ─── Sample Data ──────────────────────────────────────────────────────────────

const MOCK_INVOICES: Invoice[] = [
  { id: "1",  number: "IN-2026-0001", company: "Acme Internationals", quote: "QT-2026-0001", amount: "1.000,00 €", status: "PAID",    dueDate: "12 Apr 2026" },
  { id: "2",  number: "IN-2026-0001", company: "Acme Internationals", quote: "QT-2026-0001", amount: "1.000,00 €", status: "PAID",    dueDate: "12 Apr 2026" },
  { id: "3",  number: "IN-2026-0001", company: "Acme Internationals", quote: null,           amount: "1.000,00 €", status: "OVERDUE", dueDate: "12 Apr 2026" },
  { id: "4",  number: "IN-2026-0001", company: "Acme Internationals", quote: null,           amount: "1.000,00 €", status: "SENT",    dueDate: "12 Apr 2026" },
  { id: "5",  number: "IN-2026-0001", company: "Beta Solutions",      quote: "QT-2026-0001", amount: "2.500,00 €", status: "SENT",    dueDate: "15 Apr 2026" },
  { id: "6",  number: "IN-2026-0001", company: "Beta Solutions",      quote: null,           amount: "2.500,00 €", status: "PAID",    dueDate: "15 Apr 2026" },
  { id: "7",  number: "IN-2026-0001", company: "Beta Solutions",      quote: null,           amount: "2.500,00 €", status: "OVERDUE", dueDate: "15 Apr 2026" },
  { id: "8",  number: "IN-2026-0001", company: "Beta Solutions",      quote: "QT-2026-0001", amount: "2.500,00 €", status: "DRAFT",   dueDate: "15 Apr 2026" },
  { id: "9",  number: "IN-2026-0001", company: "Gamma Enterprises",   quote: null,           amount: "5.000,00 €", status: "SENT",    dueDate: "20 Apr 2026" },
  { id: "10", number: "IN-2026-0001", company: "Gamma Enterprises",   quote: "QT-2026-0001", amount: "5.000,00 €", status: "PAID",    dueDate: "20 Apr 2026" },
  { id: "11", number: "QT-2026-0003", company: "Gamma Enterprises",   quote: null,           amount: "5.000,00 €", status: "SENT",    dueDate: "20 Apr 2026" },
  { id: "12", number: "QT-2026-0003", company: "Gamma Enterprises",   quote: "QT-2026-0001", amount: "5.000,00 €", status: "PAID",    dueDate: "20 Apr 2026" },
];

const SAMPLE_COMPANIES = [
  "Acme Internationals",
  "Beta Solutions",
  "Gamma Enterprises",
  "Delta Corp",
  "Epsilon Industries",
];

// ─── Status badge ─────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<InvoiceStatus, { bg: string; color: string }> = {
  PAID:    { bg: "#ccefeb", color: "#00b09b" },
  OVERDUE: { bg: "#fde5f0", color: "#ee0064" },
  SENT:    { bg: "#e6e8fe", color: "#0517f8" },
  DRAFT:   { bg: "#f3f4f6", color: "#748094" },
};

function StatusBadge({ status }: { status: InvoiceStatus }) {
  const cfg = STATUS_CONFIG[status];
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "4px 6px",
        borderRadius: 4,
        fontSize: 12,
        fontWeight: 700,
        lineHeight: "16px",
        letterSpacing: "-0.24px",
        background: cfg.bg,
        color: cfg.color,
        fontFamily: "var(--font-family-base)",
        whiteSpace: "nowrap",
      }}
    >
      {status}
    </span>
  );
}

// ─── Icons ────────────────────────────────────────────────────────────────────

function SortIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0, opacity: 0.5 }}>
      <path d="M6 2L8.5 5H3.5L6 2Z" fill="currentColor" />
      <path d="M6 10L3.5 7H8.5L6 10Z" fill="currentColor" />
    </svg>
  );
}

function InvoiceNumIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1.5" y="1.5" width="11" height="11" rx="1.5" />
      <path d="M4 4h2M4 7h6M4 10h6M8 4h2" />
    </svg>
  );
}

function CompanyIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="10" height="8" rx="1" />
      <path d="M5 4V3a2 2 0 014 0v1M5 8h4" />
    </svg>
  );
}

function QuoteIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1.5" y="2.5" width="11" height="9" rx="1.5" />
      <path d="M4.5 5.5h5M4.5 7h3" />
    </svg>
  );
}

function AmountIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 2v10" />
      <path d="M9.5 4.5a2.5 2.5 0 00-5 0c0 2 5 2 5 4a2.5 2.5 0 01-5 0" />
    </svg>
  );
}

function StatusIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 4h1.5M3 7H11M3 10h8" />
      <rect x="5" y="3" width="6" height="2" rx="0.5" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1.5" y="2.5" width="11" height="10" rx="1.5" />
      <path d="M1.5 6h11M4.5 1v3M9.5 1v3" />
    </svg>
  );
}

function DotsIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
      <circle cx="3.5" cy="8" r="1.25" />
      <circle cx="8" cy="8" r="1.25" />
      <circle cx="12.5" cy="8" r="1.25" />
    </svg>
  );
}


function XIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <path d="M4 4l8 8M12 4l-8 8" />
    </svg>
  );
}

const SAMPLE_CONTACTS = ["Micheal Zeigler", "Anna Schmidt", "John Doe", "Maria Garcia"];
const SAMPLE_QUOTES = ["QT-2026-000123", "QT-2026-000124", "QT-2026-000125", "QT-2026-000126"];

function ContactAvatar({ name }: { name: string }) {
  const initials = name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();
  return (
    <span style={{
      width: 22, height: 22, borderRadius: "50%",
      background: "#0517f8", color: "#fff",
      fontSize: 10, fontWeight: 700, fontFamily: "var(--font-family-base)",
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      flexShrink: 0, letterSpacing: "-0.2px",
    }}>
      {initials}
    </span>
  );
}

// ─── New Invoice Drawer ───────────────────────────────────────────────────────

interface NewInvoiceDrawerProps {
  open: boolean;
  onClose: () => void;
  onCreate?: () => void;
}

function NewInvoiceDrawer({ open, onClose, onCreate }: NewInvoiceDrawerProps) {
  const [company, setCompany] = React.useState("Acme International");
  const [contact, setContact] = React.useState("Micheal Zeigler");
  const [linkQuote, setLinkQuote] = React.useState("QT-2026-000123");
  const [dueDate, setDueDate] = React.useState("2026-04-22");

  if (!open) return null;

  return (
    <>
      <div
        onClick={onClose}
        style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.25)", zIndex: 100 }}
      />
      <div style={{
        position: "fixed",
        top: 0,
        right: 0,
        width: 360,
        height: "100vh",
        background: "var(--color-bg-primary)",
        zIndex: 101,
        display: "flex",
        flexDirection: "column",
        boxShadow: "-4px 0 24px rgba(0,0,0,0.12)",
      }}>
        {/* Header */}
        <div style={{
          padding: "20px 24px",
          borderBottom: "1px solid var(--color-border-primary)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
        }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)", letterSpacing: "-0.32px" }}>
            New Invoice
          </span>
          <button
            onClick={onClose}
            style={{ width: 28, height: 28, border: "none", background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 6, color: "var(--color-content-secondary)" }}
          >
            <XIcon />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "24px 24px 8px" }}>
          {/* Company */}
          <SelectField
            label="Company"
            required
            value={company}
            placeholder="Select a company"
            onChange={setCompany}
            options={SAMPLE_COMPANIES.map((c) => ({ label: c, value: c }))}
            style={{ marginBottom: 20 }}
          />

          {/* Contact */}
          <SelectField
            label="Contact"
            required
            value={contact}
            placeholder="Select a contact"
            onChange={setContact}
            options={SAMPLE_CONTACTS.map((c) => ({ label: c, value: c }))}
            style={{ marginBottom: 20 }}
          />

          {/* Link Quote */}
          <SelectField
            label="Link Quote"
            value={linkQuote}
            placeholder="Select a quote"
            onChange={setLinkQuote}
            options={SAMPLE_QUOTES.map((q) => ({ label: q, value: q }))}
            description="Line items in the quotation will be carried over automatically"
            style={{ marginBottom: 20 }}
          />

          {/* Due Date */}
          <DateField
            label="Due Date"
            required
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            style={{ marginBottom: 20 }}
          />
        </div>

        {/* Footer */}
        <div style={{ padding: "16px 24px", borderTop: "1px solid var(--color-border-primary)", flexShrink: 0 }}>
          <button
            onClick={() => { onClose(); onCreate?.(); }}
            style={{
              width: "100%", height: 44, border: "none", borderRadius: 8,
              background: "var(--color-bg-brand-dark)",
              color: "#fff", fontSize: 14, fontWeight: 700,
              fontFamily: "var(--font-family-base)",
              cursor: "pointer", letterSpacing: "-0.2px",
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "var(--color-bg-brand-darker, #0311c5)"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "var(--color-bg-brand-dark)"; }}
          >
            Create Invoice
          </button>
        </div>
      </div>
    </>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

const TABS: TabFilter[] = ["All", "Sent", "Overdue", "Paid"];

interface InvoicesListPageProps {
  onOpenInvoice?: (invoiceNumber: string) => void;
  onCreateInvoice?: () => void;
}

export function InvoicesListPage({ onOpenInvoice, onCreateInvoice }: InvoicesListPageProps) {
  const [activeTab, setActiveTab] = React.useState<TabFilter>("All");
  const [hoveredRow, setHoveredRow] = React.useState<string | null>(null);
  const [sortCol, setSortCol] = React.useState<string | null>(null);
  const [sortDir, setSortDir] = React.useState<"asc" | "desc">("asc");
  const [drawerOpen, setDrawerOpen] = React.useState(false);

  const tabCounts: Record<TabFilter, number> = {
    All:     MOCK_INVOICES.length,
    Sent:    MOCK_INVOICES.filter((i) => i.status === "SENT").length,
    Overdue: MOCK_INVOICES.filter((i) => i.status === "OVERDUE").length,
    Paid:    MOCK_INVOICES.filter((i) => i.status === "PAID").length,
  };

  const filteredRows = React.useMemo(() => {
    let rows = [...MOCK_INVOICES];
    if (activeTab !== "All") {
      rows = rows.filter((i) => i.status === activeTab.toUpperCase());
    }
    if (sortCol) {
      rows.sort((a, b) => {
        const va = ((a as unknown) as Record<string, unknown>)[sortCol] as string ?? "";
        const vb = ((b as unknown) as Record<string, unknown>)[sortCol] as string ?? "";
        return sortDir === "asc" ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
      });
    }
    return rows;
  }, [activeTab, sortCol, sortDir]);

  function handleSort(col: string) {
    if (sortCol === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortCol(col);
      setSortDir("asc");
    }
  }

  const colHeaderStyle: React.CSSProperties = {
    background: "var(--color-bg-accent)",
    borderBottom: "1px solid var(--color-border-primary)",
    padding: "0 12px",
    height: 34,
    whiteSpace: "nowrap",
    textAlign: "left",
    fontWeight: "normal",
  };

  const colHeaderInner: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12,
    fontWeight: 500,
    color: "#748094",
    cursor: "pointer",
    userSelect: "none",
    fontFamily: "var(--font-family-base)",
  };

  const cellStyle: React.CSSProperties = {
    padding: "0 12px",
    height: 48,
    fontSize: 12,
    fontWeight: 500,
    color: "var(--color-content-primary)",
    fontFamily: "var(--font-family-base)",
    borderBottom: "1px solid var(--color-border-primary)",
    verticalAlign: "middle",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden" }}>
      {/* Page header */}
      <PageHeader
        breadcrumbs={[{ label: "Invoices" }]}
        searchAction={() => {}}
        primaryAction={{ label: "New Invoice", onClick: () => setDrawerOpen(true) }}
      />

      <div style={{ flex: 1, overflowY: "auto", background: "var(--color-bg-primary)" }}>

        {/* Content card with tabs + table */}
        <div style={{ margin: "24px 24px 24px", border: "1px solid var(--color-border-primary)", borderRadius: 8, overflow: "hidden" }}>

          {/* Tabs */}
          <div style={{ borderBottom: "1px solid var(--color-border-primary)", padding: "16px 0 0" }}>
            <div style={{ display: "flex", gap: 0, paddingLeft: 0 }}>
              {TABS.map((tab) => {
                const active = tab === activeTab;
                return (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    style={{
                      padding: "0 8px 12px",
                      marginRight: 4,
                      border: "none",
                      borderBottom: active ? "2px solid var(--color-bg-brand-dark)" : "2px solid transparent",
                      background: "transparent",
                      cursor: "pointer",
                      fontFamily: "var(--font-family-base)",
                      fontSize: 12,
                      fontWeight: 700,
                      color: active ? "var(--color-content-primary)" : "#a2aab8",
                      whiteSpace: "nowrap",
                      letterSpacing: "-0.24px",
                      lineHeight: "16px",
                      transition: "color 120ms ease, border-color 120ms ease",
                    }}
                  >
                    {tab} ({tabCounts[tab]})
                  </button>
                );
              })}
            </div>
          </div>

          {/* Table */}
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
              <colgroup>
                <col style={{ width: 132 }} />
                <col style={{ minWidth: 200 }} />
                <col style={{ width: 132 }} />
                <col style={{ width: 119 }} />
                <col style={{ width: 119 }} />
                <col style={{ width: 158 }} />
                <col style={{ width: 44 }} />
              </colgroup>
              <thead>
                <tr>
                  {[
                    { key: "number",  label: "Invoice",  icon: <InvoiceNumIcon />, sortable: false },
                    { key: "company", label: "Company",  icon: <CompanyIcon />,    sortable: true  },
                    { key: "quote",   label: "Quote",    icon: <QuoteIcon />,      sortable: false },
                    { key: "amount",  label: "Amount",   icon: <AmountIcon />,     sortable: true  },
                    { key: "status",  label: "Status",   icon: <StatusIcon />,     sortable: false },
                    { key: "dueDate", label: "Due Date", icon: <CalendarIcon />,   sortable: true  },
                    { key: "__actions", label: "", icon: null, sortable: false },
                  ].map(({ key, label, icon, sortable }) => (
                    <th key={key} style={{ ...colHeaderStyle }}>
                      {label ? (
                        <span
                          style={colHeaderInner}
                          onClick={() => sortable && handleSort(key)}
                        >
                          {icon}
                          {label}
                          {sortable && <SortIcon />}
                        </span>
                      ) : null}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => (
                  <tr
                    key={row.id}
                    onMouseEnter={() => setHoveredRow(row.id)}
                    onMouseLeave={() => setHoveredRow(null)}
                    onClick={() => onOpenInvoice?.(row.number)}
                    style={{
                      background: hoveredRow === row.id ? "var(--color-bg-accent)" : "var(--color-bg-primary)",
                      cursor: "pointer",
                      transition: "background 80ms ease",
                    }}
                  >
                    {/* Invoice number */}
                    <td style={{ ...cellStyle }}>
                      {row.number}
                    </td>
                    {/* Company */}
                    <td style={{ ...cellStyle }}>{row.company}</td>
                    {/* Quote */}
                    <td style={{ ...cellStyle, color: row.quote ? "var(--color-content-primary)" : "#748094" }}>
                      {row.quote ?? "-"}
                    </td>
                    {/* Amount */}
                    <td style={{ ...cellStyle }}>{row.amount}</td>
                    {/* Status */}
                    <td style={{ ...cellStyle }}>
                      <StatusBadge status={row.status} />
                    </td>
                    {/* Due Date */}
                    <td style={{ ...cellStyle, color: "#748094" }}>{row.dueDate}</td>
                    {/* Actions */}
                    <td
                      style={{ ...cellStyle, overflow: "visible", textOverflow: "unset", textAlign: "center" }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button style={{ border: "none", background: "transparent", cursor: "pointer", color: "#748094", display: "inline-flex", alignItems: "center", justifyContent: "center", padding: 4, borderRadius: 4 }}>
                        <DotsIcon />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredRows.length === 0 && (
              <div style={{
                textAlign: "center",
                padding: "48px 24px",
                color: "#9CA3AF",
                fontSize: 14,
                fontFamily: "var(--font-family-base)",
              }}>
                No invoices found
              </div>
            )}
          </div>
        </div>
      </div>

      {/* New Invoice Drawer */}
      <NewInvoiceDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onCreate={onCreateInvoice}
      />
    </div>
  );
}
